/**
 * First-visit tips for brand-new members (Jason, 2026-10-20).
 *
 * Each tip is a white speech bubble anchored to an element marked
 * `data-tip-anchor="<anchor>"`. Tips on a page show one at a time in the order
 * listed; "Got it" closes the current one, records it on the member's account
 * (users/{uid}.onboardingTips.<id>) so it never returns on any device, and
 * opens the next one, scrolling it into view.
 *
 * Who sees them (Jason, 2026-10-20): every signed-in member. Members who have
 * completed a trade or purchase (users.completedTrades > 0, which counts both)
 * know their way around, so they only get the tips marked `audience: 'all'`
 * (the newer Interests and "Never show me" features); everyone else gets the
 * full set. `?resetTips=1` on localhost clears your own dismissals.
 */

export const TIPS = {
  dashboardProfile: {
    audience: 'newcomer',
    anchor: 'profile',
    placement: 'left',
    text: 'Visit your User Profile to update address, notification, and payment details.',
  },
  dashboardBrowse: {
    audience: 'newcomer',
    anchor: 'browse',
    placement: 'auto',
    text: 'Browse and search all available pedals here.',
  },
  dashboardTradeOpportunities: {
    audience: 'newcomer',
    anchor: 'trade-opportunities',
    placement: 'below',
    text: 'These users want your pedals already. Visit here to see all Trade Opportunities.',
  },
  myPedalsWants: {
    audience: 'newcomer',
    anchor: 'my-wants-tab',
    placement: 'below',
    text: 'Swipe over to My Wants to edit saved pedals and Wants.',
  },
  offersInterests: {
    audience: 'all',
    anchor: 'interests-tab',
    placement: 'below',
    text: "When someone saves one of your pedals, their interest shows up here. Pick any of their pedals you'd trade for in return.",
  },
  addWantExclusions: {
    audience: 'all',
    anchor: 'never-show-me',
    placement: 'above',
    text: 'Optionally, exclude specific brands or keywords from your broader wants for more flexibility.',
  },
};

export function isEligibleForTips(firestoreUser) {
  return !!firestoreUser;
}

/** True once the member has completed a trade or a purchase. */
export function isExperiencedTrader(firestoreUser) {
  return Number(firestoreUser && firestoreUser.completedTrades) > 0;
}

/** Drop the newcomer-only tips for an experienced member. */
export function stepsForMember(steps, firestoreUser) {
  if (!isExperiencedTrader(firestoreUser)) return steps;
  return steps.filter((step) => (TIPS[step.id] && TIPS[step.id].audience) === 'all');
}

/** The first tip in `steps` not yet dismissed; null when it is not ready yet or all are done. */
export function nextTip(steps, dismissed) {
  for (const step of steps) {
    if (dismissed && dismissed[step.id]) continue;
    return step.ready === false ? null : step.id;
  }
  return null;
}
